// Syft ACP - Core <https://github.com/Syft-Application/syft2acp>
// © Syft Online Limited

import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import * as authActions from 'syft-acp-core/actions/auth'
import * as appActions from 'syft-acp-core/actions/app'
import LoginSSORedirect from './LoginSSORedirect'
import LoginTroubleModal from './LoginTroubleModal'

import { LoginSSOFormComponentProps as Props } from './LoginSSOForm.types'

const LoginSSOFormComponent = ({
  errorBody,
  isCheckingLogin,
  isLoggedIn,
  hasRehydrated,
  actions,
  query,
}: Props) => {
  const [openTroubleModal, setOpenTroubleModal] = useState(false)
  const [redeemStarted, setRedeemStarted] = useState(false)

  // Redeem the SSO code when Okta returns us here with one.
  useEffect(() => {
    if (query?.code) {
      setRedeemStarted(true)
      actions.consumeIndeedToken(query?.code)
    }
  }, [])

  // Auto-login: once rehydration has settled (logged-out, not mid-redeem), redirect straight to
  // Okta; identity comes from the SAML NameID at /consume.
  useEffect(() => {
    if (hasRehydrated && !isLoggedIn && !query?.code) {
      actions.beginLoginSSOEmailless()
    }
  }, [hasRehydrated, isLoggedIn, query?.code])

  const loginTrouble = () => {
    setOpenTroubleModal(true)
  }

  const closeLoginModal = () => {
    setOpenTroubleModal(false)
  }

  const message = errorBody?.error_description
  // Happy path: a brief "redirecting to Okta" spinner. If a redeem started, has settled (no longer
  // checking) and still left us logged out, it failed — show the retry screen even when the backend
  // returned no error message, so we never spin forever.
  const isRetry = (redeemStarted || !!message) && !isCheckingLogin && !isLoggedIn

  return (
    <>
      <LoginSSORedirect
        isRetry={isRetry}
        feedbackMessage={message}
        onRetry={actions.beginLoginSSOEmailless}
        onLoginTrouble={loginTrouble}
      />
      <LoginTroubleModal
        handleSubmit={actions.resetAndReload}
        isOpen={openTroubleModal}
        handleOnExit={closeLoginModal}
      />
    </>
  )
}

const LoginSSOForm = connect(
  state => ({
    errorBody: state.auth?.errorBody,
    hasRehydrated: state.localstorage?.hasRehydrated,
    isCheckingLogin: state.auth?.isCheckingLogin,
    isLoggedIn: state.auth?.isLoggedIn,
    query: state.routing?.locationBeforeTransitions.query,
  }),
  dispatch => ({
    actions: bindActionCreators(
      {
        ...authActions,
        ...appActions,
      },
      dispatch,
    ),
  }),
)(LoginSSOFormComponent)

export { LoginSSOForm }
